import React, { useContext, useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";

import AboutUs from "./components/AboutUs";
import ResponsiveAppMenu from "./components/ResponsiveAppMenu";
import ChessBasics from "./components/ChessBasics";
import ChessBenefits from "./components/ChessBenefits";
import ChessCamps from "./components/ChessCamps";
import ChessClubVenuePage from "./components/ChessClubVenuePage";
import ChessEventCard from "./pages/ChessEventCard";
import ChessCamp from "./pages/ChessCamp";
import ChessWorldPixi from "./components/ChessWorldPixi";
import ChesscampOctober2025 from "./components/ChesscampOctober2025";
import ClubsEntranceHero from "./components/ClubsEntranceHero";
import CookieConsent from "./components/CookieConsent";
import CookiePolicy from "./components/CookiePolicy";
import CookiePolicyModal from "./components/CookiePolicyModal";
import HampshireFideBlitz2025 from "./components/HampshireFideBlitz2025";
import HomeEntranceHero from "./components/HomeEntranceHero";
import JuniorClubWinchester from "./components/JuniorClubWinchester";
import LichessTeamBattleInfo from "./components/LichessTeamBattleInfo";
import Login from "./components/Login";
import OnlineMeetings from "./components/OnlineMeetings";
import Privacy from "./components/Privacy";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AppDownloadSection from "./pages/AppDownloadSection";
import Calendar from "./components/Calendar";
import Cancel from "./pages/Cancel";
import Categories from "./pages/Categories";
import Checkout from "./pages/Checkout";
import ContactPage from "./pages/ContactPage";
import Edit from "./pages/Edit";
import Edit2 from "./pages/Edit2";
import EncorageLogin from "./pages/EncorageLogin";
import EntryTraining from "./pages/EntryTraining";
import TrainingShop from "./training-payments/TrainingShop";
import PuzzleTrainingLanding from "./training-payments/PuzzleTrainingLanding";
import TrainingAccess from "./training-payments/TrainingAccess";
import TrainingAuthGate from "./training-payments/TrainingAuthGate";
import HintWithAlerts from "./pages/HintWithAlerts";
import HoodAIWeb from "./pages/HoodAIWeb";
import Play from "./pages/Play";
import PrintPuzzle from "./pages/PrintPuzzle";
import Profile from "./pages/Profile";
import Register from "./pages/Register";
import Stats from "./pages/Stats";
import Success from "./pages/Success";
import SubscriptionTerms from "./pages/SubscriptionTerms";
import { useLanguage } from "./contexts/LanguageContext";
import { UserContext } from "./contexts/UserContext";
import { BASE_URL, authHeader } from "./integrations/APIClient";
import { useOwnerQa } from "./contexts/OwnerQaContext";
import UsageAnalytics from "./analytics/UsageAnalytics";

const HoodTeam = lazy(() => import("./hood-team/HoodTeam"));
const AnalysisBeta = lazy(() => import("./pages/AnalysisBeta"));
const HoodBeginnerTraining = lazy(() => import("./pages/HoodBeginnerTraining"));
const OwnerQaLab = lazy(() => import("./pages/OwnerQaLab"));
const OwnerUsageAnalytics = lazy(() => import("./pages/OwnerUsageAnalytics"));
const LongGameTraining = lazy(() => import("./pages/LongGameTraining"));

const APP_SHELL_STYLE = {
  minHeight: "100vh",
  padding: "clamp(12px, 2vw, 24px)",
  backgroundColor: "#081a3a",
  backgroundImage:
    "linear-gradient(180deg, rgba(8, 24, 58, 0.82), rgba(6, 16, 40, 0.92)), url('/tlo.png')",
  backgroundRepeat: "no-repeat, repeat",
  backgroundSize: "cover, 320px auto",
  backgroundPosition: "center, top left",
};

const APP_INNER_STYLE = {
  maxWidth: 1220,
  margin: "0 auto",
};

const APP_COPY = {
  checkingAccount: {
    en: "Checking your account...",
    pl: "Sprawdzanie konta...",
    es: "Comprobando tu cuenta...",
  },
  home: {
    en: "Home",
    pl: "Start",
    es: "Inicio",
  },
};

const RequireAuth = ({ children }) => {
  const location = useLocation();
  const { firebaseUser, loading } = useContext(UserContext);
  const { pick } = useLanguage();

  if (loading) {
    return (
      <Container className="py-4" style={{ color: "#fff0c8" }}>
        {pick(APP_COPY.checkingAccount)}
      </Container>
    );
  }

  return firebaseUser ? (
    children
  ) : (
    <Navigate to="/login" replace state={{ from: location }} />
  );
};

function HomePageWithHero() {
  return <HomeEntranceHero />;
}

function TrainingRoute() {
  const { firebaseUser, loading } = useContext(UserContext);
  const { pick } = useLanguage();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const hasLegacySession = query.has("session") || query.has("qaSession");

  return (
    <TrainingAuthGate
      firebaseUser={firebaseUser}
      loading={loading}
      loadingFallback={
        <StandardLayout>
          <Container className="py-4" style={{ color: "#fff0c8" }}>
            {pick(APP_COPY.checkingAccount)}
          </Container>
        </StandardLayout>
      }
    >
      {hasLegacySession ? <TrainingAccess><Play /></TrainingAccess> : <Play />}
    </TrainingAuthGate>
  );
}

function TrainingLandingRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // Existing one-off sessions still return to /training?checkout=cs_...
  // The new £0.90 monthly Checkout also adds billing=success.
  return params.has("checkout") && !params.has("billing")
    ? <TrainingShop />
    : <PuzzleTrainingLanding />;
}

function HoodTeamRoute() {
  const location = useLocation();
  if (new URLSearchParams(location.search).has("session")) {
    return <RequireAuth><HoodAIRoute variant="alpha" /></RequireAuth>;
  }
  return <Suspense fallback={<div style={{padding:32, color:"white"}}>Opening Hood AI…</div>}><HoodTeam /></Suspense>;
}

function HoodAIRoute({ variant = "alpha" }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paidSessionId = params.get("session") || "";
  const qaSession = params.get("qaSession") || "";
  const simulatedSessionRequested = variant === "beta" && Boolean(qaSession);

  if (paidSessionId || simulatedSessionRequested) {
    return (
      <TrainingAccess kind="hood">
        <HoodAIWeb variant={variant} />
      </TrainingAccess>
    );
  }

  return <HoodAIWeb variant={variant} />;
}

function OwnerOnlyHoodBeta() {
  const qa = useOwnerQa();
  const [access, setAccess] = useState("checking");

  useEffect(() => {
    let cancelled = false;

    async function verifyOwner() {
      if (!qa.isOwner) {
        if (!cancelled) setAccess("denied");
        return;
      }

      try {
        const headers = await authHeader();
        const response = await fetch(`${BASE_URL}/api/owner/beta-access`, {
          method: "GET",
          headers,
        });
        const data = await response.json().catch(() => ({}));
        if (!cancelled) setAccess(response.ok && data?.allowed === true ? "allowed" : "denied");
      } catch {
        if (!cancelled) setAccess("denied");
      }
    }

    verifyOwner();
    return () => { cancelled = true; };
  }, [qa.isOwner]);

  if (access === "checking") {
    return <div style={{ minHeight: "100vh", background: "#061a34", color: "#fff", padding: 24 }}>Checking private Beta access…</div>;
  }

  return access === "allowed"
    ? <HoodAIRoute variant="beta" />
    : <Navigate to="/hood-ai-alpha" replace />;
}

function StandardLayout({ children }) {
  const { pick } = useLanguage();
  const homeButton = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 16px",
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.78)",
    background: "rgba(255, 213, 116, 0.10)",
    color: "#ffebb8",
    fontWeight: 800,
    textDecoration: "none",
    boxShadow:
      "0 0 0 1px rgba(255, 239, 183, 0.08), 0 12px 24px rgba(5, 10, 25, 0.18)",
  };

  return (
    <div style={APP_SHELL_STYLE}>
      <div style={APP_INNER_STYLE}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <Link to="/" style={homeButton}>
            {pick(APP_COPY.home)}
          </Link>
          <ResponsiveAppMenu />
        </div>
        {children}
      </div>
    </div>
  );
}

function BackButtonLayout({ children, backTo = "/", hideBackButton = false }) {
  const { pick } = useLanguage();
  const backButton = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
    padding: "10px 16px",
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.78)",
    background: "rgba(255, 213, 116, 0.10)",
    color: "#ffebb8",
    fontWeight: 800,
    textDecoration: "none",
    boxShadow:
      "0 0 0 1px rgba(255, 239, 183, 0.08), 0 12px 24px rgba(5, 10, 25, 0.18)",
  };

  return (
    <div style={APP_SHELL_STYLE}>
      <div style={APP_INNER_STYLE}>
        <div
          style={{
            display: "flex",
            justifyContent: hideBackButton ? "flex-end" : "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {!hideBackButton && (
            <Link to={backTo} style={backButton}>
              {pick(APP_COPY.home)}
            </Link>
          )}
          <ResponsiveAppMenu />
        </div>
      </div>
      {children}
    </div>
  );
}

function App() {
  return (
    <>
      <UsageAnalytics />
      <Routes>
        <Route path="/" element={<HomePageWithHero />} />
        <Route path="/hoodai" element={<Suspense fallback={<div style={{ background: "#0d2034", color: "#fff", minHeight: "100vh", padding: 24 }}>Getting your lesson ready…</div>}><HoodBeginnerTraining /></Suspense>} />
        <Route path="/world" element={<ChessWorldPixi />} />

        <Route
          path="/AppDownloadSection"
          element={
            <StandardLayout>
              <AppDownloadSection />
            </StandardLayout>
          }
        />

        <Route
          path="/entrytraining"
          element={<EntryTraining />}
        />
        <Route path="/training" element={<TrainingLandingRoute />} />
        {/* Existing one-off passes remain accessible for prior purchasers. */}
        <Route path="/training/passes" element={<TrainingShop />} />
        <Route
          path="/training/long-game"
          element={
            <RequireAuth>
              <Suspense fallback={<div style={{ minHeight: "100vh", background: "#07152d", color: "#fff", padding: 24 }}>Opening Long Game Training…</div>}>
                <LongGameTraining />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route path="/long-game" element={<Navigate to="/training/long-game" replace />} />
        <Route path="/analysis" element={<Suspense fallback={<div style={{padding:32}}>Opening AI Chess Analysis…</div>}><AnalysisBeta /></Suspense>} />
        <Route
          path="/owner-qa"
          element={
            <RequireAuth>
              <Suspense fallback={<div style={{ minHeight: "100vh", background: "#061a34", color: "#fff", padding: 24 }}>Opening QA Lab…</div>}>
                <OwnerQaLab />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="/owner-analytics"
          element={
            <RequireAuth>
              <Suspense fallback={<div style={{ minHeight: "100vh", background: "#061a34", color: "#fff", padding: 24 }}>Opening usage analytics…</div>}>
                <OwnerUsageAnalytics />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route path="/training/map" element={<EntryTraining />} />
        <Route
          path="/play"
          element={<TrainingRoute />}
        />
        <Route
          path="/play/:id"
          element={
            <RequireAuth>
              <TrainingRoute />
            </RequireAuth>
          }
        />
        <Route path="/training/hood-ai" element={<Navigate to="/hood-ai-alpha" replace />} />
        <Route path="/hood-ai" element={<HoodTeamRoute />} />
        <Route path="/hood-ai/coach" element={<RequireAuth><HoodAIRoute variant="alpha" /></RequireAuth>} />
        <Route
          path="/hood-ai-alpha"
          element={
            <RequireAuth>
              <HoodAIRoute variant="alpha" />
            </RequireAuth>
          }
        />
        <Route
          path="/hood-ai-beta"
          element={
            <RequireAuth>
              <OwnerOnlyHoodBeta />
            </RequireAuth>
          }
        />

        <Route
          path="/edit"
          element={
            <StandardLayout>
              <Edit />
            </StandardLayout>
          }
        />
        <Route
          path="/edit/:id"
          element={
            <StandardLayout>
              <Edit />
            </StandardLayout>
          }
        />
        <Route
          path="/edit2/:id"
          element={
            <StandardLayout>
              <Edit2 />
            </StandardLayout>
          }
        />
        <Route
          path="/stats"
          element={
            <StandardLayout>
              <Stats />
            </StandardLayout>
          }
        />
        <Route
          path="/contact"
          element={
            <BackButtonLayout>
              <ContactPage />
            </BackButtonLayout>
          }
        />
        <Route
          path="/hint"
          element={
            <StandardLayout>
              <HintWithAlerts />
            </StandardLayout>
          }
        />
        <Route
          path="/categories"
          element={
            <StandardLayout>
              <Categories />
            </StandardLayout>
          }
        />
        <Route
          path="/checkout"
          element={
            <StandardLayout>
              <Checkout />
            </StandardLayout>
          }
        />
        <Route path="/subscribe" element={<Navigate to="/checkout" replace />} />
        <Route
          path="/success"
          element={
            <StandardLayout>
              <Success />
            </StandardLayout>
          }
        />
        <Route
          path="/cancel"
          element={
            <StandardLayout>
              <Cancel />
            </StandardLayout>
          }
        />
        <Route
          path="/print/:puzzleid"
          element={
            <StandardLayout>
              <PrintPuzzle />
            </StandardLayout>
          }
        />
        <Route
          path="/encorage-login"
          element={
            <StandardLayout>
              <EncorageLogin />
            </StandardLayout>
          }
        />
        <Route
          path="/login"
          element={
            <BackButtonLayout>
              <Login showLogin={false} />
            </BackButtonLayout>
          }
        />
        <Route
          path="/register"
          element={
            <BackButtonLayout>
              <Register />
            </BackButtonLayout>
          }
        />
        <Route
          path="/profile"
          element={
            <StandardLayout>
              <RequireAuth>
                <Profile />
              </RequireAuth>
            </StandardLayout>
          }
        />

        <Route
          path="/cookie-policy-modal"
          element={
            <StandardLayout>
              <CookiePolicyModal />
            </StandardLayout>
          }
        />
        <Route
          path="/cookie-policy"
          element={
            <BackButtonLayout>
              <CookiePolicy />
            </BackButtonLayout>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <BackButtonLayout>
              <PrivacyPolicy />
            </BackButtonLayout>
          }
        />
        <Route
          path="/Privacy"
          element={
            <BackButtonLayout>
              <Privacy />
            </BackButtonLayout>
          }
        />
        <Route
          path="/privacy"
          element={
            <BackButtonLayout>
              <Privacy />
            </BackButtonLayout>
          }
        />
        <Route
          path="/subscription-terms"
          element={
            <BackButtonLayout>
              <SubscriptionTerms />
            </BackButtonLayout>
          }
        />

        <Route path="/chessclub" element={<ClubsEntranceHero />} />
        <Route
          path="/colden-common"
          element={
            <BackButtonLayout>
              <ChessClubVenuePage clubId="colden-common" />
            </BackButtonLayout>
          }
        />
        <Route
          path="/winchester"
          element={
            <BackButtonLayout>
              <ChessClubVenuePage clubId="winchester" />
            </BackButtonLayout>
          }
        />
        <Route
          path="/chesscamps"
          element={
            <StandardLayout>
              <ChessCamps />
            </StandardLayout>
          }
        />
        <Route
          path="/aboutus"
          element={
            <StandardLayout>
              <AboutUs />
            </StandardLayout>
          }
        />
        <Route
          path="/junior-winchester"
          element={
            <StandardLayout>
              <JuniorClubWinchester />
            </StandardLayout>
          }
        />
        <Route
          path="/lichess"
          element={
            <StandardLayout>
              <LichessTeamBattleInfo />
            </StandardLayout>
          }
        />
        <Route
          path="/meetings"
          element={
            <BackButtonLayout>
              <OnlineMeetings />
            </BackButtonLayout>
          }
        />
        <Route
          path="/calendar"
          element={
            <StandardLayout>
              <Calendar />
            </StandardLayout>
          }
        />
        <Route
          path="/event"
          element={
            <StandardLayout>
              <ChessEventCard />
            </StandardLayout>
          }
        />
        <Route
          path="/chessbasics"
          element={
            <BackButtonLayout>
              <ChessBasics />
            </BackButtonLayout>
          }
        />
        <Route
          path="/chessbenefits"
          element={
            <StandardLayout>
              <ChessBenefits />
            </StandardLayout>
          }
        />
        <Route
          path="/chesscamp"
          element={
            <StandardLayout>
              <ChessCamp />
            </StandardLayout>
          }
        />
        <Route
          path="/chesscamp-october-2025"
          element={
            <StandardLayout>
              <ChesscampOctober2025 />
            </StandardLayout>
          }
        />
        <Route
          path="/hampshire-open-junior-fide-blitz-2025"
          element={
            <StandardLayout>
              <HampshireFideBlitz2025 />
            </StandardLayout>
          }
        />

        <Route path="/ContactPage" element={<Navigate to="/contact" replace />} />
        <Route
          path="/CookiePolicyModal"
          element={<Navigate to="/cookie-policy-modal" replace />}
        />
        <Route
          path="/CookiePolicy"
          element={<Navigate to="/cookie-policy" replace />}
        />
        <Route
          path="/PrivacyPolicy"
          element={<Navigate to="/privacy-policy" replace />}
        />
        <Route
          path="/WinchesterChessClub"
          element={<Navigate to="/winchester" replace />}
        />
        <Route path="/ChessCamps" element={<Navigate to="/chesscamps" replace />} />
        <Route
          path="/JuniorClubWinchester"
          element={<Navigate to="/junior-winchester" replace />}
        />
        <Route
          path="/LichessTeamBattleInfo"
          element={<Navigate to="/lichess" replace />}
        />
        <Route
          path="/OnlineMeetings"
          element={<Navigate to="/meetings" replace />}
        />
        <Route path="/Calendar" element={<Navigate to="/calendar" replace />} />
        <Route path="/ChessEventCard" element={<Navigate to="/event" replace />} />

        <Route
          path="*"
          element={
            <StandardLayout>
              <CookieConsent />
            </StandardLayout>
          }
        />
      </Routes>
    </>
  );
}

export default App;



